import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogAPI } from '../services/api';
import Navbar from '../components/HomePage/Navbar';
import Footer from '../components/HomePage/Footer';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { ArrowLeft, Calendar, User } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const res = await blogAPI.getBySlug(slug);
        setBlog(res.data.data.blog);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Post not found' : 'Failed to load post');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  return (
    <div className="w-full bg-black overflow-x-hidden min-h-screen">
      {/* Navbar */}
      <div className="relative z-10">
        <Navbar />
      </div>

      {/* Content */}
      <section className="pt-4 sm:pt-8 pb-16 sm:pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-5 lg:px-6 xl:px-8">
          {/* Back Link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-ag-black-300 hover:text-ag-green transition-colors text-sm font-medium mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-ag-black-300 text-xl font-medium">{error}</p>
              <Link
                to="/blog"
                className="inline-block mt-4 text-ag-green hover:underline text-sm"
              >
                Browse all posts
              </Link>
            </div>
          ) : blog ? (
            <article className="animate-fadeIn">
              {/* Subject Badge */}
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-ag-green bg-ag-green/10 px-3 py-1 rounded-full mb-4">
                {blog.subject}
              </span>

              {/* Title */}
              <h1 className="font-sourceSans font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-6">
                {blog.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-ag-black-300 text-sm mb-8 pb-8 border-b border-ag-black-600">
                {blog.author && (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-ag-black-600 overflow-hidden">
                      {blog.author.photo && blog.author.photo !== 'default.jpg' ? (
                        <img
                          src={blog.author.photo}
                          alt={blog.author.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm text-ag-black-300 font-bold">
                          {blog.author.name?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="font-medium text-white">{blog.author.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {new Date(blog.publishedAt).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>

              {/* Thumbnail */}
              {blog.thumbnail?.url && (
                <div className="mb-10 rounded-xl overflow-hidden border border-ag-black-600">
                  <img
                    src={blog.thumbnail.url}
                    alt={blog.title}
                    className="w-full h-auto object-cover"
                  />
                </div>
              )}

              {/* Blog Content */}
              <div
                className="blog-content"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </article>
          ) : null}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
